import React, { useState, useRef } from 'react';
import { string, number } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './QrCodeButton.module.css';

/**
 * QrCodeButton — generates and downloads a QR code for a listing URL.
 *
 * The QR code is generated client-side using the `qrcode` npm package.
 * No server required. The QR code can be downloaded as a PNG.
 *
 * Usage (in ListingPage):
 *   <QrCodeButton url={`${MARKETPLACE_URL}/l/${slug}/${listingId}`} />
 *
 * @param {string} props.url — URL to encode in the QR code
 * @param {string} [props.title] — filename for download
 * @param {number} [props.size] — QR code size in pixels (default: 256)
 */
const QrCodeButton = ({ url, title, size, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const canvasRef = useRef(null);

  const generateQr = async () => {
    if (dataUrl) { setIsOpen(true); return; }
    setIsLoading(true);
    try {
      const QRCode = await import('qrcode');
      const url64 = await QRCode.toDataURL(url, {
        width: size,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      });
      setDataUrl(url64);
      setIsOpen(true);
    } catch (err) {
      console.error('[QrCodeButton] error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${(title || 'listing').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className={classNames(css.root, className)}>
      <button
        type="button"
        onClick={generateQr}
        disabled={isLoading}
        className={css.trigger}
      >
        <svg className={css.icon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zm-10 8H3v6h6v-6zm-2 2h2v2H5v-2zm8 2v2h2v-2h-2zm2-2h2v2h-2v-2zm0 4h2v2h-2v-2zm4-4h2v2h-2v-2zm0 4h2v2h-2v-2z"/>
        </svg>
        {isLoading ? (
          <FormattedMessage id="QrCodeButton.generating" />
        ) : (
          <FormattedMessage id="QrCodeButton.label" />
        )}
      </button>

      {isOpen && dataUrl && (
        <div className={css.modal}>
          <div className={css.overlay} onClick={() => setIsOpen(false)} />
          <div className={css.dialog}>
            <img src={dataUrl} alt="QR Code" className={css.qrImage} />
            <p className={css.urlText}>{url}</p>
            <div className={css.dialogActions}>
              <button type="button" onClick={handleDownload} className={css.downloadBtn}>
                <FormattedMessage id="QrCodeButton.download" />
              </button>
              <button type="button" onClick={() => setIsOpen(false)} className={css.closeBtn}>
                <FormattedMessage id="QrCodeButton.close" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

QrCodeButton.defaultProps = {
  title: 'listing',
  size: 256,
  className: null,
};

QrCodeButton.propTypes = {
  url: string.isRequired,
  title: string,
  size: number,
  className: string,
};

export default QrCodeButton;
